import React, { useEffect, useMemo, useState } from "react";
import "./styles.css";

import AnimateHeight from "react-animate-height";
import { NavLink, useLocation } from "react-router-dom";
import {
  FiArchive,
  FiBell,
  FiBarChart2,
  FiChevronDown,
  FiFileText,
  FiHelpCircle,
  FiHome,
  FiInbox,
  FiMail,
  FiRepeat,
  FiSettings,
  FiTrendingUp,
  FiUsers,
} from "react-icons/fi";
import { CardSubItemSidebar } from "../CardSubItemSidebar";

export interface CardSidebarProps {
  title: string;
  path?: string;
  cName?: string;
  itens?: CardSidebarProps[];
  subItens?: CardSidebarProps[];
  onNavigate?: () => void;
  level?: number;
}

const normalizeText = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const iconByTitle = (title: string) => {
  const normalizedTitle = normalizeText(title);

  if (normalizedTitle.includes("home")) return <FiHome />;
  if (normalizedTitle.includes("aporte") || normalizedTitle.includes("saque")) return <FiRepeat />;
  if (normalizedTitle.includes("ativo")) return <FiTrendingUp />;
  if (normalizedTitle.includes("extrato")) return <FiFileText />;
  if (normalizedTitle.includes("relatorio")) return <FiBarChart2 />;
  if (normalizedTitle.includes("faq")) return <FiHelpCircle />;
  if (normalizedTitle.includes("contato")) return <FiMail />;
  if (normalizedTitle.includes("cliente") || normalizedTitle.includes("funcionario")) return <FiUsers />;
  if (normalizedTitle.includes("fechamento")) return <FiArchive />;
  if (normalizedTitle.includes("solicitacao")) return <FiInbox />;
  if (normalizedTitle.includes("painel") || normalizedTitle.includes("comando")) return <FiSettings />;
  if (normalizedTitle.includes("gestao")) return <FiSettings />;
  if (normalizedTitle.includes("comunicacao")) return <FiBell />;

  return <FiFileText />;
};

const hasActiveRoute = (items: CardSidebarProps[] | undefined, pathname: string): boolean => {
  if (!items?.length) return false;

  return items.some((item) => {
    if (item.path && pathname.startsWith(item.path)) return true;
    return hasActiveRoute(item.itens || item.subItens, pathname);
  });
};

export const CardSidebar = ({
  title,
  itens,
  path,
  onNavigate,
  level = 0,
}: CardSidebarProps) => {
  const location = useLocation();
  const hasChildren = Boolean(itens?.length);
  const childIsActive = hasActiveRoute(itens, location.pathname);
  const linkIsActive = Boolean(path && location.pathname.startsWith(path));
  const isActive = linkIsActive || childIsActive;
  const [itensOpen, setItensOpen] = useState<boolean>(isActive);
  const itemIcon = useMemo(() => iconByTitle(title), [title]);

  useEffect(() => {
    if (isActive) {
      setItensOpen(true);
    }
  }, [isActive]);

  const itemClassName = `sidebar-nav-link level-${level} ${isActive ? "active" : ""}`;

  return (
    <li className="sidebar-nav-item">
      {path ? (
        <NavLink
          to={path}
          className={({ isActive: navLinkActive }) =>
            `${itemClassName} ${navLinkActive ? "active" : ""}`
          }
          onClick={onNavigate}
        >
          <span className="sidebar-nav-icon" aria-hidden="true">{itemIcon}</span>
          <span className="sidebar-nav-label">{title}</span>
        </NavLink>
      ) : (
        <button
          className={`${itemClassName} ${itensOpen ? "open" : ""}`}
          type="button"
          aria-expanded={itensOpen}
          onClick={() => setItensOpen(!itensOpen)}
        >
          <span className="sidebar-nav-icon" aria-hidden="true">{itemIcon}</span>
          <span className="sidebar-nav-label">{title}</span>
          {hasChildren && (
            <FiChevronDown className="sidebar-nav-chevron" aria-hidden="true" />
          )}
        </button>
      )}

      {hasChildren && (
        <AnimateHeight duration={240} height={itensOpen ? "auto" : 0}>
          <ul className="sidebar-nav-children">
            {itens?.map((item) => (
              item.subItens ? (
                <CardSubItemSidebar
                  key={`${item.title}-${item.path || "group"}`}
                  path={item.path}
                  subItens={item.subItens}
                  title={item.title}
                  level={level + 1}
                  onNavigate={onNavigate}
                />
              ) : (
                <CardSidebar
                  key={`${item.title}-${item.path || "group"}`}
                  title={item.title}
                  path={item.path}
                  itens={item.itens}
                  level={level + 1}
                  onNavigate={onNavigate}
                />
              )
            ))}
          </ul>
        </AnimateHeight>
      )}
    </li>
  );
};
